/**
 * CoverFeed — the image-first discovery grid, with genre/mood/style filters
 * and infinite-scroll style pagination over the (mocked) catalog.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { stagger, fadeUp } from '@/lib/motion';
import { useCoverClub } from './context/CoverClubContext';
import { useInfiniteReveal } from './hooks';
import CoverCard from './CoverCard';
import EmptyState from './EmptyState';
import { sortByTrending, sortByNewest } from './utils/coverHelpers';

const SORTS = [
  { value: 'trending', label: 'Trending' },
  { value: 'newest', label: 'Newest' },
];

export default function CoverFeed({ collectionFilter, onOpenCover }) {
  const { entries, loadFeed, getReactionCounts, myReactions, toggleReaction, isBookOnShelf, addToShelf } = useCoverClub();
  const [genre, setGenre] = useState('all');
  const [style, setStyle] = useState('all');
  const [sortBy, setSortBy] = useState('trending');

  useEffect(() => {
    loadFeed().catch((error) => console.error('[Cover Club API] Failed to load Discover feed:', error));
  }, [loadFeed]);

  const genres = useMemo(() => [...new Set(entries.map((e) => e.book.genres?.[0]).filter(Boolean))].sort(), [entries]);
  const styles = useMemo(() => [...new Set(entries.map((e) => e.style))].sort(), [entries]);

  const filtered = useMemo(() => {
    let result = entries;
    if (collectionFilter) result = result.filter((e) => e.collections?.includes(collectionFilter));
    if (genre !== 'all') result = result.filter((e) => e.book.genres?.[0] === genre);
    if (style !== 'all') result = result.filter((e) => e.style === style);
    return sortBy === 'trending' ? sortByTrending(result) : sortByNewest(result);
  }, [entries, collectionFilter, genre, style, sortBy]);

  const { visibleItems, hasMore, sentinelRef } = useInfiniteReveal(filtered);

  const clearFilters = () => { setGenre('all'); setStyle('all'); };

  return (
    <div data-testid="cover-club-feed">
      {!collectionFilter && (
        <div className="flex flex-wrap items-center gap-2.5">
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger className="w-40 rounded-full border-line bg-card text-sm" data-testid="cover-filter-genre"><SelectValue placeholder="Genre" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All genres</SelectItem>
              {genres.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={style} onValueChange={setStyle}>
            <SelectTrigger className="w-40 rounded-full border-line bg-card text-sm" data-testid="cover-filter-style"><SelectValue placeholder="Style" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All styles</SelectItem>
              {styles.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-36 rounded-full border-line bg-card text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState type="noResults" onCTAClick={clearFilters} />
      ) : (
        <>
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="show"
            className="mt-6 columns-2 gap-4 sm:columns-3 lg:columns-4"
          >
            {visibleItems.map((entry, i) => (
              <motion.div key={entry.bookId} variants={fadeUp} custom={i % 8} className="mb-4 break-inside-avoid">
                <CoverCard
                  entry={{
                    ...entry,
                    reactionCounts: getReactionCounts(entry),
                    myReaction: myReactions[entry.bookId],
                    onToggleReaction: (key) => toggleReaction(entry.bookId, key),
                    isOnShelf: isBookOnShelf(entry.bookId),
                    onSave: () => addToShelf(entry.bookId, 'shelf_tbr'),
                  }}
                  onOpen={onOpenCover}
                />
              </motion.div>
            ))}
          </motion.div>
          {hasMore && <div ref={sentinelRef} className="h-10 w-full" aria-hidden="true" />}
        </>
      )}
    </div>
  );
}
